import { XMLBaseParser } from '../../../utils/XMLBaseParser';
import { RequestTokenResponseParseError, authErrorString } from '../../AuthErrors';
import { Logger } from '../../Logger';
import { logConstants } from '../../../utils/Constants';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';

export interface RequestTokenResponse {
	readonly tokenForService: string;
	readonly tokenIssuedDate: string;
	readonly tokenExpiryDate: Date;
	readonly tokenLifeTime: string;
	readonly token: string;
	readonly tokenExpiryMillis: number;
	readonly webLogoffUrl?: string;
	readonly credentialDisplayName?: string;
	readonly authenticationInfoId?: string;
	readonly credentialUpdateService?: string;
}

export interface RequestTokenResponseJson {
	requesttokenresponse: {
		'for-service': string;
		expiry: string;
		issued: string;
		lifetime: string;
		token: string;
		webLogoffUrl?: {
			__text?: string;
			_xmlns?: string;
		};
		CredentialUpdateInformationList?: {
			CredentialUpdateInformation?: {
				CredentialDisplayName?: string;
				AuthenticationInfoId?: string;
				CredentialUpdateService?: string;
			};
			xmlns?: string;
		};
	};
}

/**
 * Returns token reponse for logs by removing token
 * @param {RequestTokenResponseJson} response
 * @return {*}  {string}
 */
export function getRequestTokenResponseForLogs(
	response: RequestTokenResponseJson
): string {
	if (response?.requesttokenresponse) {
		const responseToLog = {
			...response.requesttokenresponse,
		};
		if (responseToLog.token) {
			delete responseToLog.token;
		}
		return JSON.stringify(responseToLog);
	}
	return JSON.stringify(response);
}

/**
 * capture additional context in case of token response parsing error
 * @param {RequestTokenResponseJson} response
 */
function logRequestTokenResponseRemotely(response: RequestTokenResponseJson): void {
	if (response?.requesttokenresponse) {
		SentryLogger.getInstance().addBreadCrumb({
			category: 'aml:RequestTokenResponseParser',
			message: `RequestTokenResponse : ${getRequestTokenResponseForLogs(response)}`,
			level: Sentry.Severity.Info,
		});
	} else {
		SentryLogger.getInstance().addBreadCrumb({
			category: 'aml:RequestTokenResponseParser',
			message: `RequestTokenResponseJson: ${response}.`,
			level: Sentry.Severity.Error,
		});
	}
}

/**
 * RequestTokenResponseParser parses the response of type - application/vnd.citrix.requesttokenresponse+xml
 *  (Sample request URL: https://go.cirtix.com/Citrix/Authentication/CitrixAGBasic/Authenticate)
 */
export class RequestTokenResponseParser extends XMLBaseParser<RequestTokenResponse> {
	private static instance: RequestTokenResponseParser;

	public static getInstance(): RequestTokenResponseParser {
		if (!RequestTokenResponseParser.instance) {
			RequestTokenResponseParser.instance = new RequestTokenResponseParser();
		}
		return RequestTokenResponseParser.instance;
	}

	/**
	 * processParsedXML function is called by the abstract base class method parseXML
	 * @param {RequestTokenResponseJson} json
	 * @returns RequestTokenResponse
	 */
	processParsedXML(json: RequestTokenResponseJson): RequestTokenResponse {
		try {
			const webLogoffUrl = json.requesttokenresponse?.webLogoffUrl?.__text ?? null;
			const credentialDisplayName =
				json.requesttokenresponse?.CredentialUpdateInformationList
					?.CredentialUpdateInformation?.CredentialDisplayName;
			const authenticationInfoId =
				json.requesttokenresponse?.CredentialUpdateInformationList
					?.CredentialUpdateInformation?.AuthenticationInfoId;
			const credentialUpdateService =
				json.requesttokenresponse?.CredentialUpdateInformationList
					?.CredentialUpdateInformation?.CredentialUpdateService;
			const realm = json.requesttokenresponse['for-service'];
			const tokenExpiry = json.requesttokenresponse['expiry']
				? new Date(json.requesttokenresponse['expiry'])
				: undefined;
			const tokenExpiryMs = tokenExpiry ? tokenExpiry.getTime() : undefined;
			const requestTokenResponse: RequestTokenResponse = {
				tokenForService: realm,
				tokenIssuedDate: json.requesttokenresponse['issued'],
				tokenExpiryDate: tokenExpiry,
				tokenLifeTime: json.requesttokenresponse['lifetime'],
				token: json.requesttokenresponse['token'],
				tokenExpiryMillis: tokenExpiryMs,
				webLogoffUrl: webLogoffUrl,
				credentialDisplayName,
				authenticationInfoId,
				credentialUpdateService,
			};

			return requestTokenResponse;
		} catch (e) {
			logRequestTokenResponseRemotely(json);
			Logger.getInstance().write(
				logConstants.ERROR,
				authErrorString.DSAUTH_REQUEST_TOKEN_RESPONSE_PARSE_ERROR +
					' ' +
					getRequestTokenResponseForLogs(json)
			);
			throw new RequestTokenResponseParseError(e);
		}
	}
}
